import { Layout, Typography, Button, Select, Form, Input } from 'antd';
import classes from './ConfirmRegistration.module.scss';
import { useTranslation } from 'react-i18next';
import RegistrationTimeline from '../../components/RegistrationTimeline/RegistrationTimeline';
import { languagesValues } from 'routes/Main/Main';
import LogoType from 'assets/img';
import { useSearchParams, useNavigate } from 'react-router';
import React from 'react';
import auth from 'api/auth';
import { useAppSelector } from 'store/hook';
import cn from 'classnames';
import Spinner from 'components/Spinner/Spinner';
import external_auth from 'api/auth_external';

const { Title } = Typography;

export default function ConfirmRegistration() {
  const [isResending, setIsResending] = React.useState(false);
  const [countdown, setCountdown] = React.useState(0);
  const [isVerifying, setIsVerifying] = React.useState(false);

  const { messageApi } = useAppSelector((store) => store.windowState);  
  const [form] = Form.useForm();

  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const email = searchParams.get('email');
  const type = searchParams.get('type');
  const language = localStorage.getItem('language') || 'en';


  const onLanguageChange = (value: string) => {
    localStorage.setItem('language', value);
    i18n.changeLanguage(value);
  };

  React.useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleResendEmail = async () => {
    if (!email) {
      messageApi?.error(t('confirmRegistration.errors.noEmail'));
      return;
    }

    setIsResending(true);
    try {
      await auth.resendConfirmationLink({ email });
      messageApi?.success(t('confirmRegistration.success.emailResent'));
      setCountdown(60); 
    } catch (error: any) {
      messageApi?.error(t('confirmRegistration.errors.resendFailed'));
    } finally {
      setIsResending(false);
    }
  };

  const handleFaqClick = () => {
    navigate('/ref');
  };

  const sendCodeHandle = async () => {
    setIsVerifying(true);

    try {
      await external_auth.verifyRegister({context: 'context', email_code: form.getFieldValue('code') })
    } catch (e) {
      setIsVerifying(false);
    } finally {
      setTimeout(() => {
        setIsVerifying(false);
        form.setFields([
          {
            name: 'code',
            errors: [t('confirmRegistration.errors.invalidCode', {count: 'x'})], 
          },
        ]);
      }, 1500);
    }
  };

  const onResendBlitz = async () => {
    setIsResending(true);
    try {
      const resendAnswer = await external_auth.resendVerifyCode({email: email!});
      console.log(resendAnswer.data, '<<resendAnswer.');
      if (resendAnswer.data.status === 'failed') {
        console.log(resendAnswer.data.errors, 'resend verify code failed');
      }
    } catch (e) {
      console.log(e, 'resend verify code error');
      messageApi?.error(t('confirmRegistration.errors.resendCodeFailed'), 10);
      setIsResending(false);
    } finally {
      setIsResending(false);
    }
  }

  return (
    <div className={classes.mainWrap}>
      <Layout className={classes.confirmElem}>
        <Select
          className={classes.localizationSelect}
          defaultValue={language}
          onChange={onLanguageChange}
          style={{ minWidth: 130 }}
          options={languagesValues}
          optionLabelProp={'label'}
        />

        <div className={classes.confirmCard}>
          <RegistrationTimeline currentStep={1} />

          <div className={classes.typography}>
            <img src={LogoType} alt='Logotype' className={classes.logo} />
            <Typography>
              <Title level={3}>{t('confirmRegistration.title')}</Title>
            </Typography>
          </div>

          {type === 'code' 
            ? (
              <div className={cn(classes.confirmationMessage, classes.textStart)}>
                <div className={classes.messageText}>
                  {t('confirmRegistration.confirm.emailText1')}{' '}
                  <span className={classes.email}>{email}</span>
                </div>
                <Form
                  form={form}
                  layout='vertical'
                  className={classes.confirmForm}
                  onFinish={sendCodeHandle}
                >
                  <Form.Item
                    name={'code'}
                    label={null}
                    className={classes.codeWrap}
                    
                    rules={[{ required: true, message: t('confirmRegistration.confirm.emptyInput') }]}
                  >
                    <Input
                      suffix={isVerifying ? <Spinner size={20}/> : null}

                      placeholder={t('confirmRegistration.confirm.input')}
                      className={classes.input}
                    />
                  </Form.Item>
                  <Button
                    className={classes.resendBtn}
                    loading={isResending}
                    type='link'
                    size='small'
                    onClick={onResendBlitz}
                  >
                    {t('confirmRegistration.confirm.resend')}
                  </Button>
                  <Button
                    className={classes.sendBtn}
                    type='primary'
                    htmlType='submit'
                    loading={isVerifying}
                  >
                    {t('confirmRegistration.confirm.send')}
                  </Button>
                </Form>
              </div>
            ) : (
              <div className={classes.confirmationMessage}>
                <div className={classes.messageText}>
                  {t('confirmRegistration.message.part1')}{' '}
                  <span className={classes.email}>{email}</span>
                  {t('confirmRegistration.message.part2')}
                </div>

                <Button
                  type='primary'
                  className={classes.resendButton}
                  onClick={handleResendEmail}
                  loading={isResending}
                  disabled={!email || countdown > 0}
                >
                  {t('confirmRegistration.resendButton')}
                </Button>

                {countdown > 0 && (
                  <div className={classes.timerMessage}>
                    {t('confirmRegistration.timer.message', {
                      seconds: countdown,
                    })
                      .split('\n')
                      .map((line, index) => (
                        <div key={index}>{line}</div>
                      ))}
                  </div>
                )}
              </div>
            )
          }
        </div>

        <Button
          type='primary'
          className={classes.faqButton}
          onClick={handleFaqClick}
        >
          FAQ
        </Button>
      </Layout>
    </div>
  );
}
